import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { useSnackbar } from "notistack";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import {
  GetStockAdjustmentByIdResponse,
  StockAdjustment,
} from "subModule/src/services/inventory/interface";
import { useTranslation } from "react-i18next";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import DynamicSelect from "common/Select/Select";
import AddIcon from "@mui/icons-material/Add";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import Loader from "layout/Loader";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData {
  [key: string]: any;
}

const StockAdjustmentCreateUpdate: React.FC = () => {
  const params = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "Inventory",
    moduleLabelKey: "StockAdjustments",
    moduleHref: "/inventory/stock-adjustments",
    entityFallbackLabelKey: "StockAdjustment",
    isEdit: !!params?.id,
    action: "details",
  });
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    resetField,
    trigger,
  } = useForm<FormData>({ defaultValues: {} });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService({
    pageNumber: dropdownPage,
    size: 20,
    storeId: watch("storeId")?.value,
    search: dropdownSearchTerm
    // active: true,
  });

  const [loadingItems, setLoadingItems] = useState<boolean>(false); // Loader state for items

  const fetchItems = async () => {
    try {
      setLoadingItems(true); // Set loader to true
      const response = await itemsRefetch(); // Call the refetch function
      if (response?.data) {
        console.log("Items fetched successfully:", response.data);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoadingItems(false); // Set loader to false
    }
  };

  const { data: storesData, refetch: storesRefetch } =
    useGetStoreDropdownService({ pageNumber: dropdownPage, size: 50, title: dropdownSearchTerm });
  const [StockAdjustmentItem, setStockAdjustmentItem] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [StockAdjustment, setStockAdjustment] = useState<StockAdjustment>();
  const adjustmentTypesList = [
    { value: 1, label: t("remove") },
    { value: 2, label: t("add") },
  ];
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  useEffect(() => {
    storesRefetch();
    if (params?.id) {
      if (loading) getStockAdjustmentById();
    }
  }, [params]);
  useEffect(() => {
    if (watch("storeId")?.value) {
      setStockAdjustmentItem([]);
      setValue("StockAdjustmentItem", []);
      // itemsRefetch();
      fetchItems();
    }
  }, [watch("storeId")]);
  const getStockAdjustmentById = () => {
    InventoryProfileService.getStockAdjustmentById(params?.id)
      .then((res: any) => {
        if (res.success) {
          setLoading(false);
          setStockAdjustment(res.data as any);
          captureLabel(
            (res.data as any)?.itemCode ||
              (res.data as any)?.storeTitle ||
              (params?.id ? String(params.id) : null),
          );
          setStockAdjustmentItem([
            {
              ...res.data,
              adjustmentType: {
                value: res.data.adjustmentType,
                label:
                  adjustmentTypesList?.find(
                    (option) => option.value === res?.data?.adjustmentType
                  )?.label || res.data.adjustmentType,
              },
            },
          ]);
          setValue("StockAdjustmentItem", [
            {
              ...res.data,
              adjustmentType: {
                value: res.data.adjustmentType,
                label:
                  adjustmentTypesList?.find(
                    (option) => option.value === res?.data?.adjustmentType
                  )?.label || res.data.adjustmentType,
              },
            },
          ]);
          setValue("storeId", res.data?.storeId);
          setValue("id", params?.id);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" }
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const storeOptions = (storesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    })
  );
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    ...(params?.id
      ? []
      : [{ id: "baseItemQuantity", label: t("availableQuantity") }]),
    {
      id: "quantityAdjusted",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "unitTitle" },
    {
      id: "adjustmentType",
      label: t("adjustmentType"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "unitCostAdjusted",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "adjustmentReason",
      label: t("adjustmentReason") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
  ];
  const DeleteItem = (row: any) => {
    setStockAdjustmentItem(prevRows => {
      const newRows = prevRows.filter((rows) => rows.id !== row.id);
      setValue('StockAdjustmentItem', []);
      setTimeout(() => setValue('StockAdjustmentItem', newRows, { shouldValidate: true, shouldDirty: true }), 0);
      return newRows;
    });
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "StockAdjustmentItem") {
      const newItem = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {}
      );
      if (!params?.id) (newItem as any).id = Date.now();
      setStockAdjustmentItem((prevRows) => [...prevRows, newItem]);
    }
  };
  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string
  ) => {
    if (typeName === "StockAdjustmentItem") {
      const updatedItems = [...StockAdjustmentItem];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setStockAdjustmentItem(updatedItems);
    }
  };
  const onSubmit = (data: FormData) => {
    if (Object.values(data)?.length) {
      setIsBtnEnabled(true);
      const filteredItems = StockAdjustmentItem.filter(
        (item: any) => item?.itemName
      );
      if (filteredItems.length === 0) {
        enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItem")} />, {
          variant: "error",
        });
        setIsBtnEnabled(false);
        return;
      }
      filteredItems.every((row: any) => {
        delete row.id;
        delete row.unitTitle;
        delete row.unitPrice;
        delete row.quantity;
      });
      const payload = filteredItems.map((item) => ({
        ...item,
        adjustmentType: item.adjustmentType || 1,
        adjustmentReason: item.adjustmentReason || "",
        storeId: data.storeId.value,
      }));
      InventoryProfileService.createStockAdjustment(payload)
        .then((response: GetStockAdjustmentByIdResponse) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("StockAdjustment") +
                  " " +
                  t("saved") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" }
            );
            navigate("/inventory/stock-adjustments");
          } else
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                t(err?.message) || t(err?.data?.message) || "Something went wrong"
              }
            />,
            { variant: "error" }
          );
        }).finally(() => setIsBtnEnabled(false));
    }
  };
  const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => {
    if(action === 'searchIt') {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if(search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if(typeName === 'stores') storesRefetch().finally(() => setSearchLoading(null));
      else itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  }
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/inventory/stock-adjustments")}
        onSubmit={() => {}}
        heading={t("StockAdjustments")}
        icon={
          <InventoryIcon
            sx={{ fill: "#000", width: "28px", height: "28px" }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
      />
      <Grid
        container
        component="form"
        id="form"
        className="px-[8px] items-baseline"
        rowGap={2.5}
        columnGap={4}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container className="items-center">
          <Grid item xs={12} sm={6} className="text-start">
            <Typography
              className="font-Saira text-[15px] font-semibold"
              style={{ color: themeColors?.primary }}
            >
              {params?.id ? t("StockAdjustment") : t("newStockAdjustment")}{" "}
              {t("Details")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} className="hide-on-print">
            <div className="flex gap-2 justify-end">
              {params?.id ? null : (
                <Button
                  type="submit"
                  disabled={IsBtnEnabled}
                  className="primary"
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => onSubmit(handleSubmit(onSubmit))}
                >
                  {t("save")}
                </Button>
              )}
              {params?.id ? (
                <>
                  {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
                  <Button
                    className="secondary"
                    style={{ color: themeColors?.primary }}
                    variant="contained"
                    disableElevation
                    onClick={() =>
                      navigate(`${window.location.pathname}/print`)
                    }
                  >
                    {t("Print")}
                  </Button>
                </>
              ) : null}
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => navigate("/inventory/stock-adjustments")}
              >
                {t("Back to list")}
              </Button>
            </div>
          </Grid>
        </Grid>

        {params?.id ? (
          <Grid
            container
            className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
            style={{ background: themeColors?.primary }}
          >
            <Grid item xs={4}>
              <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
                {t("StockAdjustment")}
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t("Date")}</span>{" "}
                <span>
                  {StockAdjustment?.createdAt
                    ? Intl.DateTimeFormat(i18n.language, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        numberingSystem:
                          i18n.language === "ur" || i18n.language === "ar"
                            ? "arab"
                            : "latn",
                      }).format(new Date(StockAdjustment.createdAt))
                    : null}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <div className="flex justify-end gap-10">
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("store")}</span> <br />
                  <span className="font-medium">
                    {StockAdjustment?.storeTitle || ""}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("createdBy")}</span> <br />
                  <span className="font-medium">
                    {StockAdjustment?.createdBy || ""}
                  </span>
                </Typography>
              </div>
            </Grid>
          </Grid>
        ) : null}

        <Grid
          container
          item
          xs={12}
          gap={2.5}
          className="stockTransferDetails flex-nowrap"
        >
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="storeId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("from") + " " + t("store")}
                  {...field}
                  options={storeOptions}
                  isSearchable
                  isRequired
                  defaultPlaceholder="phSelectStore"
                  onChange={(selectedOption: any) =>
                    field.onChange(selectedOption)
                  }
                  error={errors.storeId?.message as string}
                  value={
                    params?.id
                      ? {
                          value: StockAdjustment?.storeId,
                          label: StockAdjustment?.storeTitle,
                        }
                      : watch("storeId")
                  }
                  isDisabled={params?.id ? true : false}
                  isLoading={searchLoading === "stores"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("stores", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("stores", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(storesData?.pages[0]?.data as any).last)
                      handleDropdownActions("stores", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} className="relative stockTransferDetails">
          {loading || loadingItems ? (
            <Loader />
          ) : (
            <Box className="bg-white py-2 px-[18px] rounded-[8px]">
              <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">
                {t("StockAdjustment")}
              </Typography>
              <InventoryItemsTable
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                  trigger,
                }}
                columns={columns}
                handleInputChange={handleInputChange}
                rows={StockAdjustmentItem}
                typeName="StockAdjustmentItem"
                items={itemsData?.pages[0]?.data?.content || []}
                currency={loggedInUserCurrency}
                DeleteItem={DeleteItem}
                {...{
                  params,
                  adjustmentTypesList,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
                checkStatus={params?.id ? 2 : 1}
                viewDelete={params?.id ? false : true}
                itemsResponse={itemsData || {}}
                handleDropdownActions={handleDropdownActions}
              />
              <Grid item xs={12} sm={6} className="hide-on-print">
                <div className="flex gap-2 justify-start">
                  <Button
                    className="secondary flex gap-1"
                    style={{ color: themeColors?.primary }}
                    disabled={params?.id ? true : false}
                    variant="contained"
                    disableElevation
                    onClick={() => addNewItem("StockAdjustmentItem")}
                  >
                    <AddIcon className="w-[20px] h-[20px]" />{" "}
                    {t("Add More Items")}
                  </Button>
                </div>
              </Grid>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} className="hide-on-print">
          <Divider className="bg-[#AEB3C4] mt-[8px]" />
        </Grid>
        <Grid container className="items-center hide-on-print">
          <Grid item xs={12} sm={6} className="text-start"></Grid>
          <Grid item xs={12} sm={6}>
            <div className="flex gap-2 justify-end">
              {params?.id ? null : (
                <Button
                  type="submit"
                  disabled={IsBtnEnabled || loading || loadingItems}
                  className="primary"
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => onSubmit(handleSubmit(onSubmit))}
                >
                  {t("save")}
                </Button>
              )}
              {params?.id ? (
                <Button
                  className="secondary"
                  variant="contained"
                  style={{ color: themeColors?.primary }}
                  disableElevation
                  onClick={() =>
                    navigate(`${window.location.pathname}/print`)
                  }
                >
                  {t("Print")}
                </Button>
              ) : null}
              <Button
                className="secondary"
                variant="contained"
                style={{ color: themeColors?.primary }}
                disableElevation
                onClick={() => navigate("/inventory/stock-adjustments")}
              >
                {t("Back to list")}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default StockAdjustmentCreateUpdate;
